import axios from 'axios'
import { supabase } from '../lib/supabase'

// Backend API — relative URL works on both localhost and Vercel
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

export const backendApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
})

// --- In-memory GET response cache ---
// Every page currently re-fetches on mount (no React Query on legacy pages).
// This 30-second per-URL cache makes navigation feel instant when the user
// jumps between pages or remounts a component within the TTL window.
// Any POST/PUT/DELETE/PATCH bumps a global "version" so subsequent GETs skip
// the cache — coarse but safe. Opt-out per-request via { cache: false }.
const CACHE_TTL_MS = 5 * 60_000
const responseCache = new Map() // key -> { expires, data, status, headers }
let cacheVersion = 0

function cacheKey(config) {
  const params = config.params ? JSON.stringify(config.params) : ''
  return `${config.baseURL || ''}${config.url || ''}?${params}|v${cacheVersion}`
}

// Path-prefiks som ALDRI cachet — endrer seg ofte og bug-felt
// stale-cache (sletting/upload) lurer brukeren med døde rad-ID-er.
// /vaktplan svarer per bruker (skjult kontaktinfo, stemplingsstatus) og skal
// alltid være ferskt.
// /quotes: selgeren skal se med en gang at kunden har akseptert.
// /kunde/: kundesidene svarer for én kunde og skal aldri gjenbrukes.
const CACHE_SKIP_PREFIXES = ['/document-interpreter', '/shopify-return-sync', '/billing/v3', '/order-discrepancies', '/vaktplan', '/quotes', '/henting', '/kunde/', '/documents']

function isCacheableGet(config) {
  if (!config || config.method?.toLowerCase() !== 'get') return false
  if (config.cache === false) return false
  if (config.responseType && config.responseType !== 'json') return false // blobs etc
  const url = config.url || ''
  if (CACHE_SKIP_PREFIXES.some(p => url.startsWith(p))) return false
  return true
}

// Request: short-circuit with cached response if fresh
backendApi.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession()
  if (session) {
    config.headers.Authorization = `Bearer ${session.access_token}`
  }

  // Modul 3: impersonation-token sendes med på alle backend-kall hvis aktiv.
  // Backend overstyrer da role/customerId til den impersonertes kontekst.
  const impToken = sessionStorage.getItem('imp_token')
  if (impToken) {
    config.headers['X-Impersonation-Token'] = impToken
  }

  // Fortell server om å bypasse sin cache når klient eksplisitt ber om ferske data
  if (config.cache === false) {
    config.headers['Cache-Control'] = 'no-cache'
  }

  // Hvilken cache-versjon kallet ble sendt under. Byttes brukeren mens kallet
  // er underveis (clearBackendCache), skal svaret ikke lagres for den nye.
  config.cacheVersjon = cacheVersion

  if (isCacheableGet(config)) {
    const key = cacheKey(config)
    const hit = responseCache.get(key)
    if (hit && hit.expires > Date.now()) {
      // Use axios adapter hook to return cached data without a network call
      config.adapter = () => Promise.resolve({
        data: hit.data,
        status: hit.status,
        statusText: 'OK',
        headers: hit.headers,
        config,
        request: null,
      })
    }
  }
  return config
})

// Response: store successful GETs; bust cache on mutations
backendApi.interceptors.response.use(
  (response) => {
    const cfg = response.config
    if (isCacheableGet(cfg) && response.status >= 200 && response.status < 300 && cfg.cacheVersjon === cacheVersion) {
      responseCache.set(cacheKey(cfg), {
        expires: Date.now() + CACHE_TTL_MS,
        data: response.data,
        status: response.status,
        headers: response.headers,
      })
    } else if (cfg && ['post', 'put', 'patch', 'delete'].includes(cfg.method?.toLowerCase())) {
      // Any mutation — bump version so cached GETs get skipped
      cacheVersion++
      responseCache.clear()
    }
    return response
  },
  (error) => {
    // On mutation error, don't bust cache (server didn't change anything)
    return Promise.reject(error)
  }
)

// Tømmes når brukeren byttes: innlogging, utlogging og når «Se som» eller
// «Logg på som kunde» starter og slutter (AuthContext). Nøkkelen sier ikke hvem
// som spurte, så uten dette kunne neste bruker i samme fane fått forrige
// brukers svar i opptil fem minutter.
export function clearBackendCache() {
  cacheVersion++
  responseCache.clear()
}

// Zendera direct API (brukes kun for live data som ikke går via backend)
const ZENDERA_URL = import.meta.env.VITE_ZENDERA_API_URL
const ZENDERA_KEY = import.meta.env.VITE_ZENDERA_API_KEY

export const USE_MOCK_ZENDERA = !ZENDERA_URL || !ZENDERA_KEY
export const USE_MOCK_AXIA = !import.meta.env.VITE_AXIA_WMS_API_URL

export const zenderaApi = axios.create({
  baseURL: ZENDERA_URL || 'http://localhost:3001/api',
  headers: {
    'Authorization': `apikey ${ZENDERA_KEY || ''}`,
    'Content-Type': 'application/json',
  },
})

export const axiaApi = axios.create({
  baseURL: import.meta.env.VITE_AXIA_WMS_API_URL || 'http://localhost:3001/api/axia',
  headers: {
    'Authorization': `Bearer ${import.meta.env.VITE_AXIA_WMS_API_KEY || ''}`,
    'Content-Type': 'application/json',
  },
})
